/* ============================================================================
   Slide 6a — "The Mind–Body Connection" — SHARED CONTENT (single source)
   Read by both HTLDSlide6aInner (desktop) and HTLDSlide6aMobile. Edit here
   — English and German side by side; the German object must have exactly the
   same shape as the English one.
   ========================================================================== */
import { ByLang, useL } from "i18n/lang";

export const accent6a    = "rgb(212, 83, 126)";
export const accent6aRaw  = "212, 83, 126";

export interface Seg { t: string; b?: boolean; em?: boolean; }

export interface CausationStep { stage: string; status: string; bars: number; desc: string; }

const en = {
  meta: {
    breadcrumbPre: "The Mind-Body Connection",
    breadcrumbAccent: "How stress and emotion affect how we feel",
    headerTitlePre: "The Mind–Body ",
    headerTitleAccent: "Connection",
    titlePre: "When the mind speaks, the ",
    titleAccentWord: "body",
    titlePost: " listens",
    subtitle: "— how we feel in mind and body is closely linked",
    connectedLabel: "Mind and body are connected",
    buildupLabel: "What can build up",
    chainLabel: "The chain of causation",
    chainBarLabel: "MIND & BODY · CONNECTED",
    dreamsLabel: "Where dreams come in",
  },
  causation: [
    { stage: "Feeling",   status: "ARISES",  bars: 8, desc: "A thought, emotion, or experience arises." },
    { stage: "Avoidance", status: "HABIT",   bars: 6, desc: "It is set aside rather than noticed." },
    { stage: "Tension",   status: "CARRIED", bars: 4, desc: "What is unaddressed is carried with us." },
    { stage: "Awareness", status: "RETURN",  bars: 2, desc: "Gentle attention lets us meet it consciously." },
  ] as CausationStep[],
  connected: [
    { t: "How we feel emotionally and how we feel physically are closely connected. Ongoing stress and difficult emotions can affect our sense of ease and wellbeing. " },
    { t: "Taking care of how we feel is part of taking care of ourselves as a whole.", b: true },
  ] as Seg[],
  buildup: [
    { t: "Difficult feelings, ongoing stress, and unresolved tension can quietly accumulate. Often the pattern is " },
    { t: "avoidance", b: true },
    { t: " — the habit of not noticing or setting aside what we carry." },
  ] as Seg[],
  dreams: [
    { t: "Dreams can be a window onto what we carry but rarely pause to notice. " },
    { t: "Meeting them with awareness is a way of listening to ourselves more fully.", b: true },
    { t: " This is the kind of self-exploration Dreamfulness invites." },
  ] as Seg[],
  canvas: {
    /** Hex-node labels, in order: heart, lungs, immune, skin, joints, gut. */
    nodes: ["HEART", "LUNGS", "IMMUNE", "SKIN", "JOINTS", "GUT"],
    core: "INNER EXPERIENCE",
    coreSub: "noticed · felt · understood",
    caption: "how we feel, mind and body together",
  },
};

export type Slide6aContent = typeof en;

const de: Slide6aContent = {
  meta: {
    breadcrumbPre: "Die Körper-Geist-Verbindung",
    breadcrumbAccent: "Wie Stress und Gefühle unser Befinden prägen",
    headerTitlePre: "Die Körper-Geist-",
    headerTitleAccent: "Verbindung",
    titlePre: "Wenn der Geist spricht, hört der ",
    titleAccentWord: "Körper",
    titlePost: " zu",
    subtitle: "– wie es uns seelisch und körperlich geht, hängt eng zusammen",
    connectedLabel: "Körper und Geist sind verbunden",
    buildupLabel: "Was sich aufstauen kann",
    chainLabel: "Die Wirkungskette",
    chainBarLabel: "KÖRPER & GEIST · VERBUNDEN",
    dreamsLabel: "Wo Träume ins Spiel kommen",
  },
  causation: [
    { stage: "Gefühl",      status: "ENTSTEHT",   bars: 8, desc: "Ein Gedanke, ein Gefühl oder ein Erlebnis entsteht." },
    { stage: "Vermeidung",  status: "GEWOHNHEIT", bars: 6, desc: "Es wird beiseitegeschoben statt wahrgenommen." },
    { stage: "Anspannung",  status: "GETRAGEN",   bars: 4, desc: "Was unbeachtet bleibt, tragen wir mit uns." },
    { stage: "Bewusstheit", status: "RÜCKKEHR",   bars: 2, desc: "Sanfte Achtsamkeit lässt uns ihm bewusst begegnen." },
  ],
  connected: [
    { t: "Wie wir uns seelisch fühlen und wie wir uns körperlich fühlen, ist eng miteinander verbunden. Anhaltender Stress und schwierige Gefühle können unser Wohlbefinden beeinträchtigen. " },
    { t: "Gut für unsere Gefühle zu sorgen heißt, gut für uns als Ganzes zu sorgen.", b: true },
  ],
  buildup: [
    { t: "Schwierige Gefühle, anhaltender Stress und ungelöste Spannungen können sich still ansammeln. Oft steckt " },
    { t: "Vermeidung", b: true },
    { t: " dahinter – die Gewohnheit, nicht hinzuschauen oder beiseitezuschieben, was wir mit uns tragen." },
  ],
  dreams: [
    { t: "Träume können ein Fenster zu dem sein, was wir mit uns tragen, aber selten wahrnehmen. " },
    { t: "Ihnen achtsam zu begegnen, heißt, uns selbst tiefer zuzuhören.", b: true },
    { t: " Zu genau dieser Selbsterkundung lädt Dreamfulness ein." },
  ],
  canvas: {
    nodes: ["HERZ", "LUNGE", "IMMUN", "HAUT", "GELENKE", "DARM"],
    core: "INNERES ERLEBEN",
    coreSub: "bemerkt · gefühlt · verstanden",
    caption: "wie wir uns fühlen – Körper und Geist",
  },
};

export const slide6aContent: ByLang<Slide6aContent> = { en, de };
export const useSlide6a = () => useL(slide6aContent);
